customElements.component = 'cart-discount';

if (!customElements.get(customElements.component)) {

    const SECTION_ID = 'cart-discount';

    const state = {
        refreshing: null,
        queued: false,
        silent: false,
        initialSynced: false
    };

    const matchesCode = (first, second) => String(first).toLowerCase() === String(second).toLowerCase();

    const signatureOf = (rows, codes) => `${rows}|${codes.join(',').toLowerCase()}`;

    const parseFinalSale = (element) => {
        if (!element) return [];

        try {
            return JSON.parse(element.textContent);
        } catch (error) {
            return [];
        }
    };

    const parseSection = (html) => {
        const doc = new DOMParser().parseFromString(html || '', 'text/html');
        const applied = doc.querySelector('[data-cart-discount-applied]');
        const rows = doc.querySelector('[data-cart-discount-rows]');

        return {
            applied: applied ? applied.innerHTML.trim() : '',
            rows: rows ? rows.innerHTML.trim() : '',
            codes: applied ? Array.from(applied.querySelectorAll('[data-cart-discount-code]')).map(element => element.dataset.cartDiscountCode) : [],
            finalSale: parseFinalSale(doc.querySelector('[data-cart-final-sale]'))
        };
    };

    const fetchSection = () => {
        return fetch(`${window.Shopify.routes.root}?section_id=${SECTION_ID}`, { credentials: 'same-origin' })
            .then(response => response.text())
            .then(parseSection);
    };

    const applyCodes = async (codes) => {
        const cart = await window.theme.cart.updateDiscounts(codes, SECTION_ID);
        const section = cart.sections && cart.sections[SECTION_ID] ? parseSection(cart.sections[SECTION_ID]) : await fetchSection();

        return { cart, section };
    };

    const isApplied = ({ cart, section }, code) => {
        if (Array.isArray(cart.discount_codes)) {
            const entry = cart.discount_codes.find(discount => matchesCode(discount.code, code));

            return Boolean(entry && entry.applicable);
        }

        return section.codes.some(applied => matchesCode(applied, code));
    };

    const domSignature = () => {
        const rows = document.querySelector('[data-cart-discount-rows]');
        const discount = document.querySelector('cart-discount');

        return signatureOf(rows ? rows.innerHTML.trim() : '', discount ? discount.appliedCodes() : []);
    };

    const renderSection = (section) => {
        document.querySelectorAll('cart-discount').forEach(element => element.renderApplied(section.applied));
        document.querySelectorAll('[data-cart-discount-rows]').forEach(element => {
            element.innerHTML = section.rows;
        });
    };

    const updateCartTotals = (cart) => {
        if (!cart || typeof cart.total_price === 'undefined') return;

        document.querySelectorAll('[data-subtotal]').forEach(element => {
            element.innerText = window.theme.money.format(cart.total_price);
        });

        window.dispatchEvent(new CustomEvent('change-shipping-bar-progress', { detail: { cartTotal: cart.total_price } }));
    };

    const refreshMinicart = async () => {
        const minicart = window.theme && window.theme.mini_cart;

        if (!minicart || typeof minicart._updateMinicartData !== 'function') return;

        state.silent = true;

        try {
            await minicart._updateMinicartData(null);
        } finally {
            state.silent = false;
        }
    };

    const refreshCartTable = async (replace = false) => {
        const cartTable = document.querySelector('cart-table');
        const section = cartTable && cartTable.closest('.shopify-section');

        if (!section) return;

        const sectionId = section.id.replace('shopify-section-', '');
        const html = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, { credentials: 'same-origin' }).then(response => response.text());
        const nextTable = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-table');

        if (!nextTable) return;

        const currentParts = cartTable.querySelectorAll('[data-cart-line-patch]');
        const nextParts = nextTable.querySelectorAll('[data-cart-line-patch]');

        if (replace || currentParts.length !== nextParts.length) {
            cartTable.outerHTML = nextTable.outerHTML;
            return;
        }

        nextParts.forEach(part => {
            const target = cartTable.querySelector(`[data-cart-line-patch="${part.dataset.cartLinePatch}"]`);

            if (target) {
                target.innerHTML = part.innerHTML;
            }
        });
    };

    const afterDiscountChange = async ({ cart, section }) => {
        renderSection(section);
        updateCartTotals(cart);

        await window.theme.cart.syncFinalSale(section.finalSale);
        await refreshMinicart();
        await refreshCartTable();
    };

    const refresh = () => {
        if (state.refreshing) {
            state.queued = true;
            return state.refreshing;
        }

        state.refreshing = (async () => {
            const before = domSignature();
            const section = await fetchSection();

            renderSection(section);

            const changed = await window.theme.cart.syncFinalSale(section.finalSale);

            if (changed) {
                await refreshMinicart();
            }

            if (changed || before !== signatureOf(section.rows, section.codes)) {
                await refreshCartTable();
            }
        })().catch(error => {
            console.log(error);
        }).then(() => {
            state.refreshing = null;

            if (state.queued) {
                state.queued = false;
                refresh();
            }
        });

        return state.refreshing;
    };

    document.addEventListener('cart:updated', () => {
        if (state.silent) return;

        refresh();
    });

    class CartDiscount extends HTMLElement {

        constructor() {

            super();

            this.field = this.querySelector('[data-cart-discount-field]');
            this.input = this.querySelector('[data-cart-discount-input]');
            this.applyButton = this.querySelector('[data-cart-discount-apply]');
            this.error = this.querySelector('[data-cart-discount-error]');
            this.errorText = this.querySelector('[data-cart-discount-error-text]');
            this.applied = this.querySelector('[data-cart-discount-applied]');
            this.status = this.querySelector('[data-cart-discount-status]');
            this.busy = false;

            this.initEvents();
            this.initFinalSale();
        }

        initEvents() {

            this.applyButton.addEventListener('click', event => {
                event.preventDefault();
                this.apply();
            });

            this.input.addEventListener('keydown', event => {
                if (event.key !== 'Enter') return;

                event.preventDefault();
                this.apply();
            });

            this.input.addEventListener('input', () => {
                if (this.input.getAttribute('aria-invalid') === 'true') {
                    this.clearError();
                }
            });

            this.applied.addEventListener('click', event => {
                const button = event.target.closest('[data-cart-discount-remove]');

                if (!button) return;

                event.preventDefault();
                this.remove(button.dataset.cartDiscountRemove);
            });
        }

        initFinalSale() {

            if (state.initialSynced) return;

            state.initialSynced = true;

            window.theme.cart.syncFinalSale(parseFinalSale(this.querySelector('[data-cart-final-sale]'))).then(changed => {
                if (changed) {
                    refreshCartTable(true);
                }
            });
        }

        appliedCodes() {

            return Array.from(this.applied.querySelectorAll('[data-cart-discount-code]')).map(element => element.dataset.cartDiscountCode);
        }

        renderApplied(html) {

            this.applied.innerHTML = html;
        }

        async apply() {

            if (this.busy) return;

            const code = this.input.value.trim();

            if (!code) {
                this.showError(this.dataset.messageEmpty);
                this.input.focus();
                return;
            }

            const existing = this.appliedCodes();

            if (existing.some(applied => matchesCode(applied, code))) {
                this.showError(this.dataset.messageAlreadyApplied);
                this.input.focus();
                return;
            }

            this.setBusy(true);
            this.clearError();

            try {
                let result = await applyCodes(existing.concat(code));
                const added = isApplied(result, code);
                const kept = existing.every(applied => isApplied(result, applied));

                if (!added || !kept) {
                    result = await applyCodes(existing);
                    renderSection(result.section);
                    updateCartTotals(result.cart);
                    this.showError(this.rejectedMessage(added, existing.length > 0));
                    this.input.focus();
                    return;
                }

                this.input.value = '';

                await afterDiscountChange(result);

                this.announce(this.dataset.messageApplied.replace('[code]', code));
                this.input.focus();
            } catch (error) {
                console.log(error);
                this.showError(error.isFromServer ? this.dataset.messageInvalid : this.dataset.messageError);
            } finally {
                this.setBusy(false);
            }
        }

        async remove(code) {

            if (this.busy) return;

            this.setBusy(true);

            try {
                const remaining = this.appliedCodes().filter(applied => !matchesCode(applied, code));

                await afterDiscountChange(await applyCodes(remaining));

                this.announce(this.dataset.messageRemoved.replace('[code]', code));
                this.input.focus();
            } catch (error) {
                console.log(error);
                this.announce(this.dataset.messageError);
            } finally {
                this.setBusy(false);
            }
        }

        rejectedMessage(added, hasExisting) {

            if (added) {
                return this.dataset.messageNotCombinable;
            }

            if (hasExisting) {
                return this.dataset.messageInvalidCombined;
            }

            return this.dataset.customer === 'true' ? this.dataset.messageInvalid : this.dataset.messageInvalidGuest;
        }

        setBusy(busy) {

            this.busy = busy;
            this.classList.toggle('is-loading', busy);
            this.setAttribute('aria-busy', busy ? 'true' : 'false');
            this.applyButton.setAttribute('aria-disabled', busy ? 'true' : 'false');
        }

        showError(message) {

            this.errorText.textContent = message;
            this.error.hidden = false;
            this.field.classList.add('is-invalid');
            this.input.setAttribute('aria-invalid', 'true');
            this.announce(message);
        }

        clearError() {

            this.errorText.textContent = '';
            this.error.hidden = true;
            this.field.classList.remove('is-invalid');
            this.input.removeAttribute('aria-invalid');
        }

        announce(message) {

            this.status.textContent = '';

            window.setTimeout(() => {
                this.status.textContent = message;
            }, 100);
        }
    }

    customElements.define(customElements.component, CartDiscount);
}
